import { styled } from '@linaria/react'
import React, { FC, PropsWithChildren, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { colors } from '../styles/colors'

type DialogState = 'closed' | 'opening' | 'open' | 'closing'

export const Dialog: FC<
  PropsWithChildren<{ onClose: VoidFunction; isNeedWallet?: boolean }>
> = ({ children, onClose }) => {
  const [state, setState] = useState<DialogState>('closed')

  useEffect(() => {
    setState('opening')

    // Save current overflow value and disable body scroll
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Trigger opening animation on next frame
    requestAnimationFrame(() => {
      setState('open')
    })

    return () => {
      // Restore original overflow value
      document.body.style.overflow = originalOverflow
    }
  }, [])

  const handleClose = () => {
    setState('closing')
  }

  const handleTransitionEnd = (
    event: React.TransitionEvent<HTMLDivElement>,
  ) => {
    // Only handle transition end for the backdrop itself, not child elements
    if (event.target === event.currentTarget && state === 'closing') {
      setState('closed')
      onClose()
    }
  }

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      handleClose()
    }
  }

  const modalRoot = document.getElementById('modal')

  return createPortal(
    <DialogBackdrop
      isVisible={state === 'open'}
      onClick={handleBackdropClick}
      onTransitionEnd={handleTransitionEnd}
    >
      <DialogContent isVisible={state === 'open'}>{children}</DialogContent>
    </DialogBackdrop>,
    modalRoot || document.body,
  )
}

const DialogBackdrop = styled.div<{ isVisible: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background-color: ${colors.shadowDark};
  opacity: ${props => (props.isVisible ? 1 : 0)};
  transition: opacity 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
`

const DialogContent = styled.div<{ isVisible: boolean }>`
  border-radius: 20px;
  width: 90%;
  max-width: 370px;
  transform: ${props => (props.isVisible ? 'scale(1)' : 'scale(0.95)')};
  opacity: ${props => (props.isVisible ? 1 : 0)};
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
`
